/**
 * THE NEW DESIGN'S OWN NOON / DUSK (design-system §7a, 2026-09-23).
 *
 * The live site's theme lives in localStorage `ui_theme` and is applied to
 * <html data-theme> before first paint (PR-1). The preview must NOT write it:
 * looking at dusk under /prest0design/ would otherwise turn prest0.ai dark for
 * that browser, half-dark (623 hardcoded hexes, C8), which is exactly what the
 * developer-org gate on that toggle exists to prevent. So the preview keeps
 * its choice under its own key and applies it to the new design's root
 * (`.pv[data-theme]`) only — never to <html>.
 *
 * Resolution, first match wins: a `?theme=dusk|noon` in the address (a link
 * that opens the page at dusk, for a demonstration); the visitor's own choice
 * in this browser; the device's colour scheme (D-3's "system" default, which
 * the live site adopts only at PR-6 — the preview is where that is seen first).
 *
 * On the first download (the door reads it to paint the right sky while the
 * chunk arrives), so keep it small. Every storage access is guarded: storage
 * can throw in a private window.
 */

export type PreviewTheme = 'light' | 'dark';
export type PreviewThemeChoice = PreviewTheme | 'system';

export const PREVIEW_THEME_KEY = 'prest0design_theme';

export function readPreviewThemeChoice(): PreviewThemeChoice {
  try {
    const v = window.localStorage.getItem(PREVIEW_THEME_KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

export function writePreviewThemeChoice(choice: PreviewThemeChoice): void {
  try {
    if (choice === 'system') window.localStorage.removeItem(PREVIEW_THEME_KEY);
    else window.localStorage.setItem(PREVIEW_THEME_KEY, choice);
  } catch {
    /* storage unavailable: the choice lasts for this page only */
  }
}

/** `?theme=dusk` / `?theme=noon` (or dark / light) in the address. */
export function themeFromAddress(search: string = typeof window !== 'undefined' ? window.location.search : ''): PreviewTheme | null {
  try {
    const v = new URLSearchParams(search).get('theme');
    if (v === 'dusk' || v === 'dark') return 'dark';
    if (v === 'noon' || v === 'light') return 'light';
  } catch {
    /* an unparsable query names no theme */
  }
  return null;
}

export function deviceTheme(): PreviewTheme {
  try {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  } catch {
    return 'light';
  }
}

export function resolvePreviewTheme(choice: PreviewThemeChoice = readPreviewThemeChoice()): PreviewTheme {
  const fromAddress = themeFromAddress();
  if (fromAddress) return fromAddress;
  if (choice !== 'system') return choice;
  return deviceTheme();
}
